/**
 * The shops at the place the party is standing — somebody in the world, not a panel on a sheet
 * (Maz, 2026-10-20: "a baker sells bread, an armourer swords").
 *
 * Each shopkeeper here sells what their trade stocks and nothing else, and buys what the sale rules allow.
 * Every name, price and the stock itself come from `GET /api/campaigns/:id/shops`; the client holds no price
 * list and no idea which trade sells what. When nobody here is selling, nothing renders at all.
 *
 * A purchase is paid from the caller's own purse, for themselves or for one of their retainers.
 */

import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../api.js';
import {
  priceText,
  SHOP_CATEGORY_LABELS,
  shopName,
  visibleEntries,
  type ShopEntry,
} from '../characters/ShopSection.js';
import { optionLabel } from '../characters/options.js';

export interface KeeperView {
  readonly keeperId: string;
  readonly keeperName: string;
  readonly trade: string;
  readonly tradeLabel: string;
  readonly entries: readonly ShopEntry[];
}

/** What of the caller's own sheet a shop needs: the purse, and what is in the pack that somebody would buy. */
export interface PackView {
  readonly purse: string;
  readonly items: readonly { readonly name: string; readonly shown: string; readonly quantity: number; readonly valueGp?: number; readonly resale: string }[];
}

interface SheetResponse {
  readonly character: {
    readonly inventory?: readonly { name: string; quantity: number; valueGp?: number }[];
  };
  readonly derived?: {
    readonly purse?: { readonly text: string };
    readonly resale?: readonly (string | null)[];
    readonly itemNames?: Readonly<Record<string, string>>;
  };
}

/** The pack as a shop reads it: only what would fetch a price, with what it fetches. Pure, so it can be tested. */
export function packFor(sheet: SheetResponse): PackView {
  const inventory = sheet.character.inventory ?? [];
  return {
    purse: sheet.derived?.purse?.text ?? '',
    items: inventory.flatMap((item, index) => {
      const resale = sheet.derived?.resale?.[index];
      if (!resale) return [];
      return [
        {
          name: item.name,
          shown: sheet.derived?.itemNames?.[item.name] ?? optionLabel(item.name),
          quantity: item.quantity,
          ...(item.valueGp !== undefined ? { valueGp: item.valueGp } : {}),
          resale,
        },
      ];
    }),
  };
}

export function ShopsPanel({
  campaignId,
  characterId,
  retainers,
  token,
}: {
  readonly campaignId: string;
  /** The viewer's own seated character; absent for somebody with no seat, who shops for nobody. */
  readonly characterId: string | null | undefined;
  readonly retainers: readonly { readonly characterId: string; readonly name: string }[];
  /** Anything that changes when the party may have gone somewhere else. */
  readonly token?: unknown;
}) {
  const [shops, setShops] = useState<readonly KeeperView[]>([]);

  useEffect(() => {
    if (!characterId) return;
    void api
      .get<{ shops: KeeperView[] }>(`/api/campaigns/${campaignId}/shops`)
      .then((body) => setShops(body?.shops ?? []))
      .catch(() => setShops([]));
  }, [campaignId, characterId, token]);

  if (!characterId || shops.length === 0) return null;
  return (
    <section aria-label="Shops here" className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        Shops here
      </h2>
      {shops.map((keeper) => (
        <KeeperShop key={keeper.keeperId} campaignId={campaignId} characterId={characterId} keeper={keeper} retainers={retainers} />
      ))}
    </section>
  );
}

export function KeeperShop({
  campaignId,
  characterId,
  keeper,
  retainers,
  initialOpen,
  initialPack,
}: {
  readonly campaignId: string;
  readonly characterId: string;
  readonly keeper: KeeperView;
  readonly retainers: readonly { readonly characterId: string; readonly name: string }[];
  /** Overridable for a test. */
  readonly initialOpen?: boolean;
  readonly initialPack?: PackView;
}) {
  const [open, setOpen] = useState(initialOpen ?? false);
  const [pack, setPack] = useState<PackView | null>(initialPack ?? null);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [forWho, setForWho] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(() => {
    void api
      .get<SheetResponse>(`/api/characters/${characterId}`)
      .then((sheet) => setPack(packFor(sheet)))
      .catch(() => setNotice('could not read your purse'));
  }, [characterId]);

  useEffect(() => {
    if (open && !pack) reload();
  }, [open, pack, reload]);

  const base = `/api/campaigns/${campaignId}/shops/${keeper.keeperId}`;
  const fail = (e: unknown, fallback: string) =>
    setNotice(e instanceof ApiError && e.message ? e.message : fallback);

  const buy = (entry: ShopEntry) => {
    setNotice(null);
    void api
      .post(`${base}/purchases`, { item: entry.id, quantity: 1, ...(forWho ? { forCharacterId: forWho } : {}) })
      .then(() => {
        setNotice(
          entry.priceCp === 0
            ? `Took ${shopName(entry)}; it costs nothing.`
            : `Bought ${shopName(entry)} from ${keeper.keeperName} for ${entry.price}.`,
        );
        reload();
      })
      .catch((e: unknown) => fail(e, 'the purchase failed'));
  };

  const sell = (item: PackView['items'][number]) => {
    setNotice(null);
    void api
      .post(`${base}/sales`, { item: item.name, quantity: 1, ...(item.valueGp !== undefined ? { valueGp: item.valueGp } : {}) })
      .then(() => {
        setNotice(`Sold ${item.shown} to ${keeper.keeperName} for ${item.resale}.`);
        reload();
      })
      .catch((e: unknown) => fail(e, 'the sale failed'));
  };

  const categories = [...new Set(keeper.entries.map((entry) => entry.category))];
  const shown = visibleEntries(keeper.entries, category, search);

  return (
    <div className="space-y-1 rounded-md border border-neutral-300 p-2 text-sm dark:border-neutral-700" aria-label={`${keeper.keeperName}'s shop`}>
      <p>
        <span className="font-medium">{keeper.keeperName}</span>{' '}
        <span className="text-xs text-neutral-500 dark:text-neutral-400">— {keeper.tradeLabel}</span>
      </p>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
      >
        {open ? 'Leave the shop' : `Trade with ${keeper.keeperName}`}
      </button>

      {open && (
        <div className="space-y-2">
          {pack && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              purse: <span id="shop-purse">{pack.purse}</span>
            </p>
          )}
          {notice && (
            <p role="status" id="shop-notice" className="text-sm text-amber-800 dark:text-amber-300">
              {notice}
            </p>
          )}
          {retainers.length > 0 && (
            <label className="block text-xs">
              Buying for{' '}
              <select
                value={forWho}
                onChange={(e) => setForWho(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">myself</option>
                {retainers.map((retainer) => (
                  <option key={retainer.characterId} value={retainer.characterId}>
                    {retainer.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {categories.length > 1 && (
              <select
                aria-label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">Everything {keeper.keeperName} sells</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {SHOP_CATEGORY_LABELS[c] ?? c}
                  </option>
                ))}
              </select>
            )}
            <input
              aria-label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="search"
              className="w-32 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
            />
          </div>
          <ul className="max-h-60 space-y-0.5 overflow-y-auto text-sm">
            {shown.map((entry) => (
              <li key={entry.id} className="flex items-baseline gap-2">
                <span className="flex-1">{shopName(entry)}</span>
                <span className="tabular-nums text-xs text-neutral-500 dark:text-neutral-400">{priceText(entry)}</span>
                <button
                  type="button"
                  aria-label={`Buy ${shopName(entry)}`}
                  onClick={() => buy(entry)}
                  className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs dark:border-neutral-700"
                >
                  Buy
                </button>
              </li>
            ))}
            {shown.length === 0 && <li className="text-xs text-neutral-500 dark:text-neutral-400">nothing matches</li>}
          </ul>

          {pack && pack.items.length > 0 && (
            <div>
              <p className="text-xs font-medium">Sell something</p>
              <ul className="space-y-0.5 text-sm">
                {pack.items.map((item) => (
                  <li key={`${item.name}-${item.valueGp ?? ''}`} className="flex items-baseline gap-2">
                    <span className="flex-1">
                      {item.shown}
                      {item.quantity > 1 && <span className="text-xs text-neutral-500 dark:text-neutral-400"> ×{item.quantity}</span>}
                    </span>
                    <button
                      type="button"
                      aria-label={`Sell ${item.shown}`}
                      onClick={() => sell(item)}
                      className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs text-emerald-700 dark:border-neutral-700 dark:text-emerald-400"
                    >
                      sell for {item.resale}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
